import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import {
  type Finding,
  findingSchema,
  type Recommendation,
  recommendationSchema,
} from './finding-dto';

/**
 * Pin one recommendation for yourself. `loopId`/`runId` name the run the reader was looking at: the
 * server snapshots the card from that run rather than from client-supplied text.
 */
export const pinBodySchema = z.object({
  loopId: z.number().int().positive().nullable(),
  runId: z.string().min(1),
  findingId: z.string().min(1),
  recommendationId: z.string().min(1),
});
export type PinBody = z.output<typeof pinBodySchema>;

/** Without `loopId`, every pin the caller holds in the org (the All loops view). */
export const pinListQuerySchema = z.object({
  loopId: z.coerce.number().int().positive().optional(),
});
export type PinListQuery = z.output<typeof pinListQuerySchema>;

/**
 * The card exactly as it was on the run when it was pinned: the whole finding (headline, statement,
 * evidence, every recommendation) and the pinned recommendation itself. Never rewritten by later
 * runs; that is the point of a pin.
 */
export interface PinSnapshot {
  finding: Finding;
  recommendation: Recommendation;
}

// Documents the shape for the OpenAPI spec. Stored snapshots are served as written and are not
// re-validated on read: a finding pinned under an older schema must still render.
export const pinSnapshotSchema = z.object({
  finding: findingSchema,
  recommendation: recommendationSchema,
});

export const pinDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  recommendationId: z.string(),
  /** The run the card was pinned from. Soft reference: the run may be pruned; the pin outlives it. */
  runId: z.string(),
  /** Order within the caller's pins for the loop; a new pin goes last. */
  position: z.number().int(),
  snapshot: pinSnapshotSchema,
  ...timestampDTOSchema.shape,
});
export type PinDTO = Omit<z.infer<typeof pinDTOSchema>, 'snapshot'> & { snapshot: PinSnapshot };
